/**
 * Branded Logo Component
 *
 * Displays the tenant's logo or falls back to default.
 * Uses TenantContext for dynamic branding.
 * Optimized to prevent repeated failed image requests.
 */

import { useState, useMemo, useCallback } from 'react';
import { useTenant } from '../../context/TenantContext';
import defaultLogo from '../../assets/logo.png';

// Session-stable cache buster (same for entire session to prevent repeated requests)
const sessionCacheBuster = Date.now();

const BrandedLogo = ({
  width = '200px',
  height = 'auto',
  style = {},
  className = '',
  showBrandName = false
}) => {
  const { tenant, loading } = useTenant();
  const [hasError, setHasError] = useState(false);
  const [displayFailed, setDisplayFailed] = useState(false);
  const imageUrl = (!displayFailed && tenant?.logoDisplayUrl) || tenant?.logoUrl;

  // Memoize logo URL to prevent recalculation on every render
  // Use session-stable cache buster instead of Date.now() on every render
  const logoSrc = useMemo(() => {
    if (hasError || !imageUrl) {
      return defaultLogo;
    }
    // Skip cache-busting for base64 data URLs (stored in DB, always fresh)
    if (imageUrl.startsWith('data:') || /\/api\/tenants\/\d+\/logo\?v=[a-f0-9]{16}(?:&w=400)?$/.test(imageUrl)) {
      return imageUrl;
    }
    // Add session-stable cache-busting for URL-based logos
    return imageUrl + (imageUrl.includes('?') ? '&' : '?') + '_t=' + sessionCacheBuster;
  }, [imageUrl, hasError]);

  const brandName = tenant?.brandName || 'Empower Care';

  // Handle image load error - only try once, then use default
  const handleError = useCallback((e) => {
    if (!displayFailed && tenant?.logoDisplayUrl && tenant?.logoUrl) {
      setDisplayFailed(true);
      e.target.src = tenant.logoUrl;
      return;
    }
    if (!hasError) {
      setHasError(true);
      e.target.src = defaultLogo;
    }
  }, [hasError, displayFailed, tenant?.logoDisplayUrl, tenant?.logoUrl]);

  // Reset error state if tenant logo URL changes
  useMemo(() => {
    setHasError(false);
    setDisplayFailed(false);
  }, [tenant?.logoUrl, tenant?.logoDisplayUrl]);

  // Show placeholder while loading
  if (loading) {
    return (
      <div
        className={`branded-logo-placeholder ${className}`}
        style={{
          width,
          height: height === 'auto' ? '60px' : height,
          backgroundColor: '#f0f0f0',
          borderRadius: '8px',
          animation: 'pulse 1.5s infinite',
          ...style
        }}
      />
    );
  }

  return (
    <div className={`branded-logo-container ${className}`} style={style}>
      <img
        src={logoSrc}
        alt={`${brandName} Logo`}
        width={!hasError ? tenant?.logoDimensions?.width : undefined}
        height={!hasError ? tenant?.logoDimensions?.height : undefined}
        style={{
          width,
          height,
          objectFit: 'contain',
        }}
        onError={handleError}
      />
      {showBrandName && (
        <span
          className="brand-name-text"
          style={{
            marginTop: '8px',
            fontSize: '14px',
            color: tenant?.primaryColor,
            fontWeight: '500'
          }}
        >
          {brandName}
        </span>
      )}
    </div>
  );
};

export default BrandedLogo;
